/**
 * Cart Drawer Kit Manager
 * Manages kit display in the cart drawer/slide-out panel
 */
class CartDrawerKitManager {
  constructor() {
    console.log('🛒 CartDrawerKitManager constructor called');
    this.init();
  }

  init() {
    console.log('Cart Drawer Kit Manager initializing');
    
    // Load kit data from localStorage
    this.loadKitData();
    
    if (this.hasGroupedCollection) {
      console.log('Has grouped collection: true');
      console.log('Proceeding with drawer kit organization');
      this.organizeDrawerByKits();
    } else {
      console.log('No grouped collection found, skipping drawer kit organization');
    }
  }

  loadKitData() {
    const rawData = localStorage.getItem('athlete_kits');
    console.log('Raw drawer localStorage data:', rawData);
    
    if (rawData) {
      try {
        this.kitData = JSON.parse(rawData);
        console.log('Loaded kit data in drawer:', this.kitData);
        this.hasGroupedCollection = this.kitData.hasGroupedCollection || false;
        this.currentKit = this.kitData.currentKit || null;
        console.log('Has grouped collection:', this.hasGroupedCollection);
        console.log('Current kit:', this.currentKit);
      } catch (error) {
        console.error('Error parsing kit data:', error);
        this.hasGroupedCollection = false;
      }
    } else {
      this.hasGroupedCollection = false;
    }
  }

  async organizeDrawerByKits() {
    console.log('Organizing cart drawer by kits');
    
    // Find cart drawer items container
    const drawerItemsContainer = document.querySelector('#CartDrawer-CartItems, .cart-drawer__items, [data-cart-drawer-items]');
    if (!drawerItemsContainer) {
      console.log('❌ Cart drawer items container not found');
      return;
    }
    
    console.log('✅ Cart drawer items container found:', drawerItemsContainer);
    
    // Fetch cart data
    const response = await fetch('/cart.js');
    const cartData = await response.json();
    console.log('Drawer cart data:', cartData);
    
    if (cartData.items.length === 0) {
      console.log('No items in cart');
      return;
    }
    
    const { kitGroups, legacyItems } = this.groupItemsByKit(cartData.items);
    console.log('Drawer kit groups:', kitGroups);
    console.log('Drawer legacy items:', legacyItems);
    
    // Only show kit sections if there are actually grouped items
    const hasGroupedItems = Object.keys(kitGroups).length > 0;
    console.log('🔍 Drawer has grouped items:', hasGroupedItems);
    
    if (hasGroupedItems) {
      console.log('✅ Found grouped items - showing drawer kit organization');
      this.renderDrawerKitSections(kitGroups, legacyItems, drawerItemsContainer);
    } else {
      console.log('ℹ️ No grouped items found - using legacy drawer display');
      // Remove any existing kit sections in drawer
      this.removeDrawerKitSections();
    }
  }

  groupItemsByKit(items) {
    console.log('🔍 Grouping drawer items by kit...');
    console.log('🔍 Total drawer cart items:', items.length);
    
    const kitGroups = {};
    const legacyItems = [];
    
    items.forEach((item, index) => {
      console.log(`🔍 Drawer Item ${index + 1}: ${item.title}`);
      console.log(`🔍 Drawer Item ${index + 1} properties:`, item.properties);
      
      const kitGroupName = item.properties?._kit_group_name || item.properties?.kit_group_name;
      
      if (kitGroupName) {
        console.log(`✅ Drawer Item ${index + 1} has kit group name, adding to group: ${kitGroupName}`);
        
        if (!kitGroups[kitGroupName]) {
          kitGroups[kitGroupName] = {
            name: kitGroupName,
            items: [],
            complete: false
          };
        }
        
        kitGroups[kitGroupName].items.push(item);
      } else {
        console.log(`➡️ Drawer Item ${index + 1} has no kit group name, adding to legacy items`);
        legacyItems.push(item);
      }
    });
    
    return { kitGroups, legacyItems };
  }

  renderDrawerKitSections(kitGroups, legacyItems, container) {
    console.log('Rendering drawer kit sections...');
    
    if (Object.keys(kitGroups).length === 0) {
      console.log('No kit groups to render in drawer');
      return;
    }
    
    // Create kit sections HTML for drawer (more compact)
    let kitSectionsHTML = '';
    
    Object.values(kitGroups).forEach(kit => {
      kitSectionsHTML += this.renderDrawerKitSection(kit);
    });
    
    // Add management section
    kitSectionsHTML += `
      <div class="cart-drawer-kit-management">
        <button class="add-another-athlete-btn drawer-btn">
          + Shop for Another Athlete
        </button>
      </div>
    `;
    
    console.log('Final drawer kit sections HTML:', kitSectionsHTML);
    
    // Insert kit sections at the top of the drawer
    const kitContainer = document.createElement('div');
    kitContainer.className = 'cart-drawer-kit-sections';
    kitContainer.innerHTML = kitSectionsHTML;
    
    console.log('Inserting drawer kit container into DOM...');
    container.insertBefore(kitContainer, container.firstChild);
    
    console.log('Drawer kit sections rendered successfully');
  }

  renderDrawerKitSection(kit) {
    console.log('🎨 Rendering drawer kit section for:', kit.name);
    
    const statusIcon = kit.complete ? '✅' : '⚠️';
    const statusText = kit.complete ? 'Complete' : `Missing items`;
    
    return `
      <div class="cart-drawer-kit-section" data-kit-id="${kit.id}">
        <div class="cart-drawer-kit-header">
          <h4 class="cart-drawer-kit-title">${kit.name} Kit ${statusIcon}</h4>
          <span class="cart-drawer-kit-status ${kit.complete ? 'complete' : 'incomplete'}">${statusText}</span>
        </div>
        <div class="cart-drawer-kit-items">
          ${kit.items.map(item => this.renderDrawerKitItem(item)).join('')}
        </div>
      </div>
    `;
  }

  renderDrawerKitItem(item) {
    return `
      <div class="cart-drawer-kit-item">
        <span class="cart-drawer-kit-item-name">${item.product_title}</span>
        <span class="cart-drawer-kit-item-quantity">Qty: ${item.quantity}</span>
      </div>
    `;
  }

  removeDrawerKitSections() {
    console.log('🧹 Removing existing drawer kit sections');
    const existingKitSections = document.querySelector('.cart-drawer-kit-sections');
    if (existingKitSections) {
      existingKitSections.remove();
      console.log('✅ Drawer kit sections removed');
    }
  }
}

// Initialize when cart drawer is opened
document.addEventListener('DOMContentLoaded', function() {
  console.log('🛒 Cart Drawer Kit Manager DOM loaded');
  
  // Try to find cart drawer elements
  const possibleDrawers = [
    '#CartDrawer',
    '.cart-drawer', 
    '[data-cart-drawer]',
    '#cart-drawer',
    '.drawer--cart',
    '[data-drawer="cart"]'
  ];
  
  let foundDrawer = null;
  for (const selector of possibleDrawers) {
    const drawer = document.querySelector(selector);
    if (drawer) {
      foundDrawer = drawer;
      console.log('🛒 Found cart drawer:', selector, drawer);
      break;
    }
  }
  
  if (!foundDrawer) {
    console.log('🛒 No cart drawer found, trying alternative approach');
    // Alternative: Listen for cart updates and check if drawer is visible
    document.addEventListener('cart:updated', () => {
      console.log('🛒 Cart updated, checking for drawer');
      setTimeout(() => {
        const visibleDrawer = document.querySelector('[style*="display: block"], [style*="visibility: visible"], .active, .open');
        if (visibleDrawer && (visibleDrawer.id.includes('cart') || visibleDrawer.className.includes('cart'))) {
          console.log('🛒 Found visible cart drawer after update');
          new CartDrawerKitManager();
        }
      }, 200);
    });
    return;
  }
  
  // Listen for cart drawer opening
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        const cartDrawer = foundDrawer;
        if (cartDrawer && (cartDrawer.classList.contains('active') || cartDrawer.classList.contains('open') || cartDrawer.classList.contains('is-open'))) {
          console.log('🛒 Cart drawer opened, initializing kit manager');
          setTimeout(() => {
            new CartDrawerKitManager();
          }, 100); // Small delay to ensure drawer is fully rendered
        }
      }
    });
  });

  // Start observing
  observer.observe(foundDrawer, { attributes: true, attributeFilter: ['class'] });
});

console.log('🛒 Cart Drawer Kit Manager script loaded'); 